import { setToastObj } from '@/store/reducers/alertsSlice';
import { addDays, format, isValid, subDays } from 'date-fns';
import { dispatch, getState } from '@/store/store';
import { appOwnerEnum, roleEnums, taskSectionsData } from './constant';
import moment from 'moment';
import axios from 'axios';
import { config } from '@/config/config';
import { ErrorMessage } from '@/types/apiResponse';
import { FormattedTaskInstance, taskEnumType } from '@/types/Task';
import { Venue } from '@/types/SharedComponent';
// import { ROUTES } from './routerRoutes';

export const getLocalStorageItem = <T>(key: string, defaultValue: T | null = null, isParse: boolean = true): T | string | null => {
  try {
    const value = localStorage.getItem(key);
    return (isParse && value ? JSON.parse(value) : value) ?? defaultValue;
  } catch (e) {
    console.error('getItemFromLocalStorage:', e);
    return defaultValue;
  }
};

export const setItemInLocalStorage = (key: string, value: any, isStringify: boolean = true): void => {
  const storedValue = isStringify ? JSON.stringify(value) : value;
  localStorage.setItem(key, storedValue);
};

export const handleLogout = (): void => {
  localStorage.clear();
  // window.location.href = ROUTES.AUTH_SIGN_IN;
};

export const handleCatchMessages = (error: any): void => {
  if (!axios.isCancel(error)) {
    if (error?.status === 429) {
      dispatch(setToastObj({ message: error.response?.data?.errors[0]?.message || 'Unknown error', severity: 'error' }));
    } else if (error?.status === 403) {
      dispatch(setToastObj({ message: error.response?.data?.message || 'Unknown error', severity: 'error' }));
    } else {
      console.log('error', error);
      dispatch(setToastObj({ message: 'Oops! Something went wrong', severity: 'error' }));
    }
  }
};

export const handleErrorMessages = (errors?: ErrorMessage[]): void => {
  if (errors) {
    const message = errors?.map(e => e.message + '\n').join('') || 'Oops! Something went wrong.';

    dispatch(setToastObj({ message, severity: 'error' }));
  }
};

export const checkIsStaffUser = (): boolean => {
  const userTypeId: any = getState().User.userDetail;
  const id: number = userTypeId?.typeId;
  return id === roleEnums.staff.id;
};

export const checkIsAppOwner = (): boolean => {
  const userTypeId: any = getState().User.userDetail;
  const id: number = userTypeId?.typeId;
  return id === appOwnerEnum.appOwner.id;
};
export const checkIsSuperAdmin = (): boolean => {
  const userTypeId: any = getState().User.userDetail;
  const id: number = userTypeId?.typeId;
  return id === roleEnums.superAdmin.id;
};

export const checkIsAdmin = (): boolean => {
  const userTypeId: any = getState().User.userDetail;
  const id: number = userTypeId?.typeId;
  return id === roleEnums.admin.id;
};

export const checkIsHeadOffice = (venueId: any) => {
  return venueId === 'head_office';
};

export const getInitials = (name: string, defaultVal = '-') => {
  if (!name) return defaultVal;
  name = name.trim();

  return (
    name
      .split(' ')
      .map(part => part.trim()?.[0]?.toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join('') || defaultVal
  );
};

export const calculateDueMessage = (dueDate: any, isUser?: boolean) => {
  const momentObj = moment(dueDate);
  if (!momentObj.isValid()) return '';

  if (isUser) {
    return momentObj.fromNow();
  }

  const dueDateTime = momentObj.startOf('day');
  const now = moment().startOf('day');
  const daysDifference = dueDateTime.diff(now, 'days');
  if (daysDifference < 0) {
    return 'Expired';
  } else if (daysDifference === 0) {
    return 'Due Today';
  } else if (daysDifference === 1) {
    return 'Due Tomorrow';
  }
  return `Due in ${daysDifference} days`;
};

export function downloadCSVFile({ filename, headers, data }: { filename: string; headers: string[]; data: (string | number)[][] }) {
  const dangerousChars = ['=', '+', '-', '@'];
  const escapeAndSanitize = (val: string | number) => {
    let str = String(val);
    if (str && dangerousChars.includes(str.charAt(0))) {
      str = `'${str}`;
    }
    return `"${str.replace(/"/g, '""')}"`;
  };
  const csvRows = [];
  csvRows.push(headers.map(escapeAndSanitize).join(','));
  for (let i = 0; i < data.length; i += 1) {
    csvRows.push(data[i].map(escapeAndSanitize).join(','));
  }
  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const sanitizeUrl = (url: string): string => {
  // Basic implementation or integrate with a library like DOMPurify
  // For example, you could do:
  const allowedProtocols = ['http:', 'https:'];
  try {
    const parsedUrl = new URL(url);
    return allowedProtocols.includes(parsedUrl.protocol) ? url : '';
  } catch (error) {
    return '';
  }
};

export function sanitizeForCSV(value: any): string {
  let str = value ? String(value) : '';
  // If the value starts with a potentially dangerous character, prepend a single quote.
  if (/^[=+\-@]/.test(str)) {
    str = `'${str}`;
  }
  return str;
}

export const getStatus = (certificationData: any, configId: string) => {
  return certificationData?.some((cert: any) => cert.configurationDocumentId === configId) ? 'Valid' : 'Not Valid';
};

export const getDocumentDetails = (certificationData: any, configId: string) => {
  return certificationData?.find((cert: any) => cert.configurationDocumentId === configId);
};

export const isDocumentExpired = (expiryDate?: string): boolean => {
  if (!expiryDate) return false;

  try {
    const today = new Date();
    const expiry = new Date(expiryDate);

    if (isNaN(expiry.getTime())) return false;

    return expiry < today;
  } catch {
    return false;
  }
};

export function getFormattedDate(value: string | Date | null | undefined, defaultValue: string = '', includeTime: boolean = false): string {
  if (!value) return defaultValue;

  try {
    const date = new Date(value);
    const formatStr = includeTime ? `${config.dateFormat}, hh:mm a` : config.dateFormat;
    return format(date, formatStr);
  } catch {
    return defaultValue;
  }
}

export const formatNotificationText = (text: string, extras: any = {}) => {
  if (!text || !extras) return text;

  return text
    .replace(/DOCUMENT_NAME/g, extras.certificateDocumentName || 'DOCUMENT_NAME')
    .replace(/STAFF_NAME/g, extras.staffName || 'STAFF_NAME');
};

export const generateChartDates = (type: 'monthly' | 'yearly', count: number): string[] => {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const date =
      type === 'monthly'
        ? new Date(now.getFullYear(), now.getMonth() - (count - 1 - i), 1)
        : new Date(now.getFullYear() - (count - 1 - i), 1);
    return date.toISOString().split('T')[0];
  });
};

export const getTaskDataLabel = (obj: taskEnumType, typeId: number | string): string => {
  if (!typeId) return '';

  return Object.values(obj).find((t: { id: number }) => t.id === typeId)?.label ?? '';
};

export function getEnumId<T extends { id: number }>(list: T[], targetId: number | undefined): number | string {
  return list.find(item => item.id === targetId)?.id ?? 'Unknown';
}

export const buildTaskSections = (groupedData: any) => {
  return taskSectionsData.map(section => {
    const data = groupedData?.[section.key] || [];

    return { ...section, data, subtitle: section.getSubtitle(), emptyMessage: section.getEmptyMessage() };
  });
};

export function assignToGroup(
  instance: FormattedTaskInstance,
  dueDate: Date | null,
  skippedAt: Date | null,
  completedAt: Date | null,
  groupedData: Record<string, FormattedTaskInstance[]>,
  isIndividual: boolean,
): void {
  const today = new Date(new Date().toISOString().split('T')[0]);
  const in7Days = new Date(today);
  in7Days.setDate(today.getDate() + 7);

  const in30Days = new Date(today);
  in30Days.setDate(today.getDate() + 30);

  if (completedAt || skippedAt) {
    groupedData.completed.push(instance);
  } else if (dueDate) {
    if (isIndividual) {
      if (dueDate < today) {
        groupedData.overdue.push(instance);
      } else {
        groupedData.today.push(instance);
      }
    } else if (dueDate < today) {
      groupedData.overdue.push(instance);
    } else if (dueDate.toDateString() === today.toDateString()) {
      groupedData.today.push(instance);
    } else if (dueDate > today && dueDate <= in7Days) {
      groupedData.thisWeek.push(instance);
    } else if (dueDate > in7Days && dueDate <= in30Days) {
      groupedData.thisMonth.push(instance);
    }
  }
}

export const getDateDiffInDays = (startDate: string | Date, endDate: string | Date): number => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
};

export const getOffsetDateRange = (startDate: string | Date, endDate: string | Date, periodOffset: number): { start: Date; end: Date } => {
  const parsedStart = new Date(startDate);
  const parsedEnd = new Date(endDate);

  if (!isValid(parsedStart) || !isValid(parsedEnd)) {
    const today = new Date();
    return { start: today, end: today };
  }

  const days = getDateDiffInDays(parsedStart, parsedEnd);
  const baseEnd = new Date(parsedEnd);
  let start: Date;
  let end: Date;

  if (periodOffset === 0) {
    start = new Date(parsedStart);
    end = new Date(parsedEnd);
  } else if (periodOffset < 0) {
    end = subDays(baseEnd, Math.abs(periodOffset) * days);
    start = subDays(end, days - 1);
  } else {
    start = addDays(baseEnd, (periodOffset - 1) * days + 1);
    end = addDays(start, days - 1);
  }

  return { start, end };
};

type SortDirection = 'asc' | 'desc';

interface LocalSort<T> {
  key: keyof T;
  direction: SortDirection;
}

export function sortByKey<T>(items: T[], localSort: LocalSort<T>): T[] {
  return [...items].sort((a, b) => {
    const aVal = a[localSort.key];
    const bVal = b[localSort.key];

    const aStr = aVal !== null ? String(aVal) : '';
    const bStr = bVal !== null ? String(bVal) : '';

    return localSort.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  });
}

export const downloadFile = async (fileOrUrl: string | Blob, filename?: string | null, fileExtension?: string | null) => {
  try {
    let blob: Blob;

    if (typeof fileOrUrl === 'string') {
      const response = await fetch(fileOrUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      blob = await response.blob();
    } else {
      blob = fileOrUrl;
    }

    let safeName = filename?.trim();

    if (!safeName) {
      safeName = fileExtension ? `download.${fileExtension.replace(/^\./, '')}` : 'download-file';
    }
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(href);
  } catch (error) {
    console.error('Download failed:', error);
  }
};

export const formatVenues = (venues: any[]): Venue[] => {
  if (!Array.isArray(venues)) return [];
  return venues.map(venue => ({
    id: String(venue.id),
    value: String(venue.id),
    name: venue.name,
    label: venue.name,
    compliance: venue.averageComplianceScore ? Math.round(venue.averageComplianceScore) : 0,
  }));
};

export function calculateGrowth(current: number, previous: number): number {
  if (previous <= 0) return 0;
  return Math.round(((current - previous) / previous) * 100);
}

export const formatPlanRevenue = (billingPlans: any[], planId: string | number, billingCycle: number): string => {
  if (!billingPlans?.length || !planId) return '£0.00';

  const plan = billingPlans.find(p => String(p.id) === String(planId));
  if (!plan) return '£0.00';

  if (billingCycle === plan.monthlyBillingCycle) {
    return `${plan.monthlyPrice}/mo`;
  } else if (billingCycle === plan.yearlyBillingCycle) {
    return `${plan.yearlyPrice}/yr`;
  }

  return '£0.00';
};

export const normalizeFileExtension = (file: File): File => {
  const nameParts = file.name.split('.');

  if (nameParts.length > 1) {
    const extension = nameParts.pop()?.toLowerCase();
    const baseName = nameParts.join('.');
    const newFileName = `${baseName}.${extension}`;
    return new File([file.slice(0, file.size)], newFileName, { type: file.type });
  }
  return file;
};
